import React, { useState, useMemo } from 'react';
import { X, DollarSign, Plus, Trash2, Droplets, Edit2, ArrowDown } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Updated gutter pricing — includes both materials AND labor
const GUTTER_PRICES = {
  'seamless_5in': { price: 11.00, label: '5" Gutters (Mat. + Labor)' },
  'seamless_6in': { price: 13.00, label: '6" Gutters (Mat. + Labor)' }
};
const GUTTER_GUARD_PRICES = {
  'guards_5in': { price: 11.00, label: '5" Gutter Guards (Mat. + Labor)' },
  'guards_6in': { price: 13.00, label: '6" Gutter Guards (Mat. + Labor)' }
};

// Default downspout pricing
const DOWNSPOUT_PRICE_PER_LF = 12.00;

const AdditionalChargesModal = ({
  isOpen,
  onClose,
  estimateId,
  estimate,
  onSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Story / Steep
  const [numStories, setNumStories] = useState('1');
  const [storyChargeType, setStoryChargeType] = useState('percent');
  const [storyChargeAmount, setStoryChargeAmount] = useState('');
  const [steepRoofCharge, setSteepRoofCharge] = useState(false);
  const [steepRoofChargeType, setSteepRoofChargeType] = useState('percent');
  const [steepRoofChargeAmount, setSteepRoofChargeAmount] = useState('');

  // Custom line-item edits (description overrides + extra costs per line item)
  const [lineItemEdits, setLineItemEdits] = useState({});

  // Other charges
  const [otherCharges, setOtherCharges] = useState([]);
  const [otherChargeDescription, setOtherChargeDescription] = useState('');
  const [otherChargeType, setOtherChargeType] = useState('dollar');
  const [otherChargeAmount, setOtherChargeAmount] = useState('');

  // Gutter state
  const [includeGutters, setIncludeGutters] = useState(false);
  const [gutterType, setGutterType] = useState('seamless_5in');
  const [gutterLinearFt, setGutterLinearFt] = useState('');
  const [includeGutterGuards, setIncludeGutterGuards] = useState(false);
  const [gutterGuardType, setGutterGuardType] = useState('guards_5in');

  // Downspout state
  const [includeDownspouts, setIncludeDownspouts] = useState(false);
  const [downspoutCount, setDownspoutCount] = useState('');
  const [downspoutLinearFt, setDownspoutLinearFt] = useState('');
  const [downspoutPricePerLf, setDownspoutPricePerLf] = useState(String(DOWNSPOUT_PRICE_PER_LF));

  // Eave measurement from satellite
  const eaveLength = useMemo(() => {
    return estimate?.satellite_data?.total_eave_length || 0;
  }, [estimate]);

  // Load existing charges on open
  React.useEffect(() => {
    if (isOpen && estimate?.additional_charges) {
      const ac = estimate.additional_charges;
      setNumStories(String(ac.num_stories || 1));
      setStoryChargeType(ac.story_charge_type || 'percent');
      setStoryChargeAmount(ac.story_charge_amount ? String(ac.story_charge_amount) : '');
      setSteepRoofCharge(!!ac.steep_roof_charge);
      setSteepRoofChargeType(ac.steep_roof_charge_type || 'percent');
      setSteepRoofChargeAmount(ac.steep_roof_charge_amount ? String(ac.steep_roof_charge_amount) : '');
      setOtherCharges(ac.other_charges || []);
      setLineItemEdits(ac.line_item_edits || {});
      if (ac.gutter_addon) {
        setIncludeGutters(true);
        setGutterType(ac.gutter_addon.gutter_type || 'seamless_5in');
        setGutterLinearFt(ac.gutter_addon.gutter_lf ? String(ac.gutter_addon.gutter_lf) : '');
        setIncludeGutterGuards(!!ac.gutter_addon.include_guards);
        setGutterGuardType(ac.gutter_addon.guard_type || 'guards_5in');
      }
      if (ac.downspout_addon) {
        setIncludeDownspouts(true);
        setDownspoutCount(ac.downspout_addon.count ? String(ac.downspout_addon.count) : '');
        setDownspoutLinearFt(ac.downspout_addon.linear_ft ? String(ac.downspout_addon.linear_ft) : '');
        setDownspoutPricePerLf(ac.downspout_addon.price_per_lf ? String(ac.downspout_addon.price_per_lf) : String(DOWNSPOUT_PRICE_PER_LF));
      }
    } else if (isOpen) {
      setNumStories('1'); setStoryChargeType('percent'); setStoryChargeAmount('');
      setSteepRoofCharge(false); setSteepRoofChargeType('percent'); setSteepRoofChargeAmount('');
      setOtherCharges([]); setLineItemEdits({});
      setIncludeGutters(false); setGutterType('seamless_5in'); setGutterLinearFt('');
      setIncludeGutterGuards(false); setGutterGuardType('guards_5in');
      setIncludeDownspouts(false); setDownspoutCount(''); setDownspoutLinearFt('');
      setDownspoutPricePerLf(String(DOWNSPOUT_PRICE_PER_LF));
    }
    setOtherChargeDescription(''); setOtherChargeType('dollar'); setOtherChargeAmount('');
  }, [isOpen, estimate]);

  const basePrice = useMemo(() => {
    return estimate?.pricing?.total || estimate?.total_cost || estimate?.pricing?.subtotal || 0;
  }, [estimate]);

  const salesTaxRate = useMemo(() => estimate?.pricing?.sales_tax_rate || 0, [estimate]);

  // Calculate preview
  const { previewTotal, previewBreakdown, previewTaxOnCharges, lineItemExtraTotal } = useMemo(() => {
    let total = 0;
    const breakdown = [];

    // Story charge
    if (parseInt(numStories) > 1 && storyChargeAmount) {
      const amt = parseFloat(storyChargeAmount);
      if (!isNaN(amt) && amt > 0) {
        const val = storyChargeType === 'percent' ? (basePrice * amt / 100) : amt;
        total += val;
        breakdown.push({ label: `${numStories}-Story Charge`, amount: val });
      }
    }

    // Steep roof
    if (steepRoofCharge && steepRoofChargeAmount) {
      const amt = parseFloat(steepRoofChargeAmount);
      if (!isNaN(amt) && amt > 0) {
        const val = steepRoofChargeType === 'percent' ? (basePrice * amt / 100) : amt;
        total += val;
        breakdown.push({ label: 'Steep Roof Charge', amount: val });
      }
    }

    // Line item extra costs
    let lineExtra = 0;
    Object.values(lineItemEdits).forEach(edit => {
      const extra = parseFloat(edit.extra_cost) || 0;
      if (extra > 0) {
        lineExtra += extra;
        breakdown.push({ label: `${edit.custom_description || edit.original_description || 'Line item'} (added cost)`, amount: extra });
      }
    });
    total += lineExtra;

    // Other charges
    otherCharges.forEach(charge => {
      const val = charge.type === 'percent' ? (basePrice * charge.amount / 100) : charge.amount;
      total += val;
      breakdown.push({ label: charge.description, amount: val });
    });

    // Pending other charge
    if (otherChargeDescription && otherChargeAmount) {
      const amt = parseFloat(otherChargeAmount);
      if (!isNaN(amt) && amt > 0) {
        const val = otherChargeType === 'percent' ? (basePrice * amt / 100) : amt;
        total += val;
        breakdown.push({ label: `${otherChargeDescription} (pending)`, amount: val, isPending: true });
      }
    }

    // Gutters
    if (includeGutters && gutterLinearFt) {
      const lf = parseFloat(gutterLinearFt);
      if (!isNaN(lf) && lf > 0) {
        const gutterCost = lf * (GUTTER_PRICES[gutterType]?.price || 11);
        total += gutterCost;
        breakdown.push({ label: GUTTER_PRICES[gutterType]?.label || 'Gutters', amount: gutterCost, isGutter: true });
        if (includeGutterGuards) {
          const guardCost = lf * (GUTTER_GUARD_PRICES[gutterGuardType]?.price || 11);
          total += guardCost;
          breakdown.push({ label: GUTTER_GUARD_PRICES[gutterGuardType]?.label || 'Gutter Guards', amount: guardCost, isGutter: true });
        }
      }
    }

    // Downspouts
    if (includeDownspouts && downspoutLinearFt) {
      const lf = parseFloat(downspoutLinearFt);
      const pricePerLf = parseFloat(downspoutPricePerLf) || DOWNSPOUT_PRICE_PER_LF;
      if (!isNaN(lf) && lf > 0) {
        const dsCost = lf * pricePerLf;
        total += dsCost;
        const countStr = downspoutCount ? ` (${downspoutCount} pcs)` : '';
        breakdown.push({ label: `Downspouts${countStr}`, amount: dsCost, isGutter: true });
      }
    }

    // Sales tax on all additional charges
    const taxOnCharges = salesTaxRate > 0 ? round2(total * salesTaxRate / 100) : 0;
    const totalWithTax = round2(total + taxOnCharges);

    return { previewTotal: totalWithTax, previewBreakdown: breakdown, previewTaxOnCharges: taxOnCharges, lineItemExtraTotal: lineExtra };
  }, [basePrice, salesTaxRate, numStories, storyChargeAmount, storyChargeType, steepRoofCharge, steepRoofChargeAmount, steepRoofChargeType, otherCharges, otherChargeDescription, otherChargeAmount, otherChargeType, includeGutters, gutterType, gutterLinearFt, includeGutterGuards, gutterGuardType, includeDownspouts, downspoutCount, downspoutLinearFt, downspoutPricePerLf, lineItemEdits]);

  // Build line items from satellite data for editing — MUST be before early return
  const detailedLineItems = useMemo(() => {
    const items = [];
    const sd = estimate?.satellite_data || {};
    const sqft = sd.square_footage || 0;
    const ridge = sd.total_ridge_length || 0;
    const valley = sd.total_valley_length || 0;
    const eave = sd.total_eave_length || 0;
    if (sqft > 0) {
      items.push({ key: 'tearoff', description: 'Remove existing roofing (tear-off)', qty: Math.round(sqft), unit: 'SF' });
      items.push({ key: 'disposal', description: 'Debris removal and disposal', qty: Math.round(sqft), unit: 'SF' });
      items.push({ key: 'underlayment', description: 'Synthetic underlayment', qty: Math.round(sqft), unit: 'SF' });
      items.push({ key: 'roofing', description: 'Roofing material', qty: Math.round(sqft), unit: 'SF' });
    }
    if (ridge > 0) items.push({ key: 'ridge', description: 'Ridge cap shingles', qty: Math.round(ridge), unit: 'LF' });
    if (valley > 0) items.push({ key: 'valley', description: 'Valley flashing', qty: Math.round(valley), unit: 'LF' });
    if (eave > 0) items.push({ key: 'drip_edge', description: 'Drip edge', qty: Math.round(eave), unit: 'LF' });
    items.push({ key: 'labor', description: 'Installation labor', qty: Math.round(sqft || 0), unit: 'SF' });
    return items;
  }, [estimate]);

  if (!isOpen) return null;

  const addOtherCharge = () => {
    if (!otherChargeDescription || !otherChargeAmount) { toast.error('Enter description and amount'); return; }
    const amt = parseFloat(otherChargeAmount);
    if (otherChargeType === 'percent' && amt > 100) { toast.error('Percentage cannot exceed 100%'); return; }
    setOtherCharges(prev => [...prev, { description: otherChargeDescription, type: otherChargeType, amount: amt }]);
    setOtherChargeDescription(''); setOtherChargeAmount('');
  };

  const removeOtherCharge = (index) => setOtherCharges(prev => prev.filter((_, i) => i !== index));

  const updateLineItemEdit = (key, field, value) => {
    setLineItemEdits(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  // Auto-fill eave measurement for gutters
  const autoFillEaves = () => {
    if (eaveLength > 0) {
      setGutterLinearFt(String(Math.round(eaveLength)));
      toast.success(`Set gutter LF to ${Math.round(eaveLength)} from roof eaves measurement`);
    } else {
      toast.error('No eave measurements available for this estimate');
    }
  };

  const handleApply = async () => {
    try {
      setIsProcessing(true);

      let finalOtherCharges = [...otherCharges];
      if (otherChargeDescription && otherChargeAmount) {
        const amt = parseFloat(otherChargeAmount);
        if (!isNaN(amt) && amt > 0) {
          finalOtherCharges.push({ description: otherChargeDescription, type: otherChargeType, amount: amt });
        }
      }

      const finalBreakdown = previewBreakdown.map(item => ({
        label: item.label.replace(' (pending)', ''),
        amount: item.amount
      }));

      const newTotal = basePrice + previewTotal;

      let gutterData = null;
      if (includeGutters && gutterLinearFt) {
        const lf = parseFloat(gutterLinearFt);
        if (!isNaN(lf) && lf > 0) {
          gutterData = {
            gutter_type: gutterType,
            gutter_lf: lf,
            include_guards: includeGutterGuards,
            guard_type: includeGutterGuards ? gutterGuardType : null
          };
        }
      }

      let downspoutData = null;
      if (includeDownspouts && downspoutLinearFt) {
        const lf = parseFloat(downspoutLinearFt);
        if (!isNaN(lf) && lf > 0) {
          downspoutData = {
            count: parseInt(downspoutCount) || 0,
            linear_ft: lf,
            price_per_lf: parseFloat(downspoutPricePerLf) || DOWNSPOUT_PRICE_PER_LF
          };
        }
      }

      const payload = {
        num_stories: parseInt(numStories),
        story_charge_type: storyChargeType,
        story_charge_amount: storyChargeAmount ? parseFloat(storyChargeAmount) : 0,
        steep_roof_charge: steepRoofCharge,
        steep_roof_charge_type: steepRoofChargeType,
        steep_roof_charge_amount: steepRoofChargeAmount ? parseFloat(steepRoofChargeAmount) : 0,
        other_charges: finalOtherCharges,
        line_item_edits: lineItemEdits,
        gutter_addon: gutterData,
        downspout_addon: downspoutData,
        breakdown: finalBreakdown,
        additional_charges_total: previewTotal,
        sales_tax_on_charges: previewTaxOnCharges,
        sales_tax_rate: salesTaxRate,
        new_total: newTotal
      };

      await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/apply-additional-charges`, payload);
      toast.success('Additional charges applied successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error applying additional charges:', error);
      toast.error('Failed to apply additional charges');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-green-600" />
            Additional Charges & Line Item Edits
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">

          {/* ── Line Item Edits ── */}
          {detailedLineItems.length > 0 && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <Label className="font-semibold flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-indigo-600" />
                Line Item Descriptions & Additional Costs
              </Label>
              <p className="text-xs text-indigo-600 mt-1 mb-3">
                Customize the description or add an extra cost to any line item. These appear on the detailed report.
              </p>
              <div className="space-y-2">
                {detailedLineItems.map(item => {
                  const edit = lineItemEdits[item.key] || {};
                  return (
                    <div key={item.key} className="grid grid-cols-12 gap-2 items-center bg-white rounded px-3 py-2 border border-indigo-100">
                      <div className="col-span-6">
                        <Input
                          placeholder={item.description}
                          value={edit.custom_description || ''}
                          onChange={(e) => updateLineItemEdit(item.key, 'custom_description', e.target.value)}
                          className="text-sm h-9"
                        />
                      </div>
                      <div className="col-span-2 text-xs text-gray-500 text-center">{item.qty} {item.unit}</div>
                      <div className="col-span-4">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+$</span>
                          <Input
                            type="number"
                            placeholder="0.00"
                            value={edit.extra_cost || ''}
                            onChange={(e) => updateLineItemEdit(item.key, 'extra_cost', e.target.value)}
                            className="text-sm h-9 pl-8"
                            min={0}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Story Charge ── */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label className="font-semibold">Multi-Story Charge</Label>
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div>
                <Label className="text-sm">Stories</Label>
                <select value={numStories} onChange={(e) => setNumStories(e.target.value)} className="w-full border rounded-lg p-2">
                  <option value="1">1 Story</option>
                  <option value="2">2 Stories</option>
                  <option value="3">3+ Stories</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">Type</Label>
                <select value={storyChargeType} onChange={(e) => setStoryChargeType(e.target.value)} className="w-full border rounded-lg p-2">
                  <option value="percent">Percentage</option>
                  <option value="dollar">Fixed Amount</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">Amount</Label>
                <Input type="number" value={storyChargeAmount} onChange={(e) => setStoryChargeAmount(e.target.value)} placeholder={storyChargeType === 'percent' ? '10' : '500'} min={0} />
              </div>
            </div>
          </div>

          {/* ── Steep Roof ── */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={steepRoofCharge} onChange={(e) => setSteepRoofCharge(e.target.checked)} id="steep-roof" />
              <Label htmlFor="steep-roof" className="font-semibold cursor-pointer">Steep Roof Charge</Label>
            </div>
            {steepRoofCharge && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-sm">Type</Label>
                  <select value={steepRoofChargeType} onChange={(e) => setSteepRoofChargeType(e.target.value)} className="w-full border rounded-lg p-2">
                    <option value="percent">Percentage</option>
                    <option value="dollar">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Amount</Label>
                  <Input type="number" value={steepRoofChargeAmount} onChange={(e) => setSteepRoofChargeAmount(e.target.value)} placeholder={steepRoofChargeType === 'percent' ? '15' : '750'} min={0} />
                </div>
              </div>
            )}
          </div>

          {/* ── Other Charges ── */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label className="font-semibold">Other Charges</Label>
            <p className="text-xs text-gray-500 mt-1 mb-3">Add as many custom line items as needed.</p>
            {otherCharges.length > 0 && (
              <div className="mb-3 space-y-2">
                {otherCharges.map((charge, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-2.5 rounded border">
                    <span className="text-sm">{charge.description}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{charge.type === 'percent' ? `${charge.amount}%` : formatCurrency(charge.amount)}</span>
                      <button onClick={() => removeOtherCharge(index)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <Label className="text-xs">Description</Label>
                <Input placeholder="e.g. Permit Fee" value={otherChargeDescription} onChange={(e) => setOtherChargeDescription(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOtherCharge(); } }} data-testid="other-charge-description" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Type</Label>
                <select value={otherChargeType} onChange={(e) => setOtherChargeType(e.target.value)} className="w-full border rounded-lg p-2 h-10" data-testid="other-charge-type">
                  <option value="dollar">$</option>
                  <option value="percent">%</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Amount</Label>
                <Input type="number" placeholder="500" value={otherChargeAmount} onChange={(e) => setOtherChargeAmount(e.target.value)} min={0} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOtherCharge(); } }} data-testid="other-charge-amount" />
              </div>
              <div className="col-span-3">
                <Button onClick={addOtherCharge} className="w-full bg-gray-700 hover:bg-gray-800 h-10" data-testid="add-other-charge-btn"><Plus className="w-4 h-4 mr-1" /> Add Charge</Button>
              </div>
            </div>
          </div>

          {/* ── Gutters & Guards ── */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={includeGutters} onChange={(e) => setIncludeGutters(e.target.checked)} id="include-gutters" data-testid="include-gutters-checkbox" />
              <Label htmlFor="include-gutters" className="font-semibold cursor-pointer flex items-center gap-2">
                <Droplets className="w-4 h-4 text-blue-600" /> Gutters & Gutter Guards
              </Label>
            </div>
            <p className="text-xs text-blue-700 mb-3">Pricing includes both materials and labor per linear foot.</p>

            {includeGutters && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Gutter Size</Label>
                    <select value={gutterType} onChange={(e) => { setGutterType(e.target.value); if (e.target.value === 'seamless_5in') setGutterGuardType('guards_5in'); if (e.target.value === 'seamless_6in') setGutterGuardType('guards_6in'); }} className="w-full border rounded-lg p-2" data-testid="gutter-type-select">
                      <option value="seamless_5in">5" ($11/LF)</option>
                      <option value="seamless_6in">6" ($13/LF)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm">Linear Feet</Label>
                    <Input type="number" value={gutterLinearFt} onChange={(e) => setGutterLinearFt(e.target.value)} placeholder={eaveLength > 0 ? String(Math.round(eaveLength)) : 'e.g. 150'} min={0} data-testid="gutter-linear-ft-input" />
                  </div>
                  <div className="flex items-end">
                    {eaveLength > 0 && (
                      <Button variant="outline" size="sm" onClick={autoFillEaves} className="w-full h-10 text-blue-700 border-blue-300 hover:bg-blue-100" data-testid="auto-fill-eaves-btn">
                        <ArrowDown className="w-3 h-3 mr-1" /> Use Eaves ({Math.round(eaveLength)} LF)
                      </Button>
                    )}
                  </div>
                </div>

                {gutterLinearFt && parseFloat(gutterLinearFt) > 0 && (
                  <div className="text-sm text-blue-800 font-medium">
                    Gutter Cost: {formatCurrency(parseFloat(gutterLinearFt) * (GUTTER_PRICES[gutterType]?.price || 11))}
                  </div>
                )}

                <div className="pt-3 border-t border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" checked={includeGutterGuards} onChange={(e) => setIncludeGutterGuards(e.target.checked)} id="include-guards" data-testid="include-guards-checkbox" />
                    <Label htmlFor="include-guards" className="cursor-pointer text-sm">Include Gutter Guards</Label>
                  </div>
                  {includeGutterGuards && gutterLinearFt && parseFloat(gutterLinearFt) > 0 && (
                    <div className="text-sm text-blue-800 font-medium">
                      Guard Cost: {formatCurrency(parseFloat(gutterLinearFt) * (GUTTER_GUARD_PRICES[gutterGuardType]?.price || 11))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Downspouts ── */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={includeDownspouts} onChange={(e) => setIncludeDownspouts(e.target.checked)} id="include-downspouts" data-testid="include-downspouts-checkbox" />
              <Label htmlFor="include-downspouts" className="font-semibold cursor-pointer flex items-center gap-2">
                <ArrowDown className="w-4 h-4 text-blue-600" /> Downspouts
              </Label>
            </div>
            <p className="text-xs text-blue-700 mb-3">Manually enter count and linear feet for downspout pricing.</p>

            {includeDownspouts && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Number of Downspouts</Label>
                  <Input type="number" value={downspoutCount} onChange={(e) => setDownspoutCount(e.target.value)} placeholder="e.g. 4" min={0} data-testid="downspout-count-input" />
                </div>
                <div>
                  <Label className="text-sm">Total Linear Feet</Label>
                  <Input type="number" value={downspoutLinearFt} onChange={(e) => setDownspoutLinearFt(e.target.value)} placeholder="e.g. 40" min={0} data-testid="downspout-lf-input" />
                </div>
                <div>
                  <Label className="text-sm">Price per LF ($)</Label>
                  <Input type="number" value={downspoutPricePerLf} onChange={(e) => setDownspoutPricePerLf(e.target.value)} placeholder="12" min={0} step="0.50" data-testid="downspout-price-input" />
                </div>
                {downspoutLinearFt && parseFloat(downspoutLinearFt) > 0 && (
                  <div className="col-span-3 text-sm text-blue-800 font-medium">
                    Downspout Cost: {formatCurrency(parseFloat(downspoutLinearFt) * (parseFloat(downspoutPricePerLf) || DOWNSPOUT_PRICE_PER_LF))}
                    {downspoutCount && ` (${downspoutCount} downspouts)`}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Preview ── */}
          {previewBreakdown.length > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <Label className="font-semibold text-blue-800">Preview</Label>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Base Price (incl. existing tax):</span>
                  <span>{formatCurrency(basePrice)}</span>
                </div>
                {previewBreakdown.map((item, i) => (
                  <div key={i} className={`flex justify-between text-sm ${item.isGutter ? 'text-blue-700' : 'text-green-700'}`}>
                    <span>+ {item.label}:</span>
                    <span>{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                {previewTaxOnCharges > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>+ Sales Tax on additional ({salesTaxRate}%):</span>
                    <span>{formatCurrency(previewTaxOnCharges)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold pt-2 border-t border-blue-200">
                  <span>New Total:</span>
                  <span>{formatCurrency(basePrice + previewTotal)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleApply} disabled={isProcessing || previewBreakdown.length === 0} className="flex-1 bg-green-600 hover:bg-green-700">
            {isProcessing ? 'Applying...' : 'Apply Charges'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdditionalChargesModal;
